import {
    AlertCircle,
    AlertTriangle,
    BarChart3,
    CheckCircle2,
    Clock,
    ExternalLink,
    Globe,
    MoreVertical,
    Settings,
    XCircle
} from "lucide-react";
import {useState} from "react";

export default function SiteRow({ site }) {
    const [showActions, setShowActions] = useState(false);

    // Configuration des statuts
    const statusConfig = {
        active: {
            icon: CheckCircle2,
            color: "text-green-500",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
            label: "Actif",
            description: "En ligne et fonctionnel"
        },
        inactive: {
            icon: XCircle,
            color: "text-red-500",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            label: "Inactif",
            description: "Hors ligne ou erreur"
        },
        warning: {
            icon: AlertTriangle,
            color: "text-yellow-500",
            bgColor: "bg-yellow-50",
            borderColor: "border-yellow-200",
            label: "Avertissement",
            description: "Performance dégradée"
        },
        error: {
            icon: AlertCircle,
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            label: "Erreur",
            description: "Problème critique"
        },
        loading: {
            icon: Clock,
            color: "text-blue-500",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            label: "Chargement",
            description: "Vérification en cours"
        }
    };

    const status = statusConfig[site.status] || statusConfig.inactive;
    const StatusIcon = status.icon;

    // Formatage de l'URL pour l'affichage
    const displayUrl = site.url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');

    // Formatage des nombres
    const formatNumber = (num) => {
        if (!num && num !== 0) return "0";
        return Number(num).toLocaleString();
    };

    // Calcul du pourcentage d'uptime (exemple)
    const uptimePercentage = site.uptime || Math.random() * 20 + 80; // Valeur exemple

    return (
        <tr className="hover:bg-gray-50 transition-colors duration-150">
            {/* Colonne Site */}
            <td className="py-4 px-6">
                <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center border border-blue-200">
                            <Globe className="h-5 w-5 text-blue-600" />
                        </div>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                                {site.name}
                            </h3>
                            {site.ssl && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  SSL
                </span>
                            )}
                        </div>
                        <div className="flex items-center mt-1">
                            <a
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-gray-500 hover:text-blue-600 flex items-center truncate"
                            >
                                {displayUrl}
                                <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                        </div>
                        <div className="flex items-center mt-2 space-x-4">
              <span className="text-xs text-gray-400">
                ID: {site.id || site.siteKey}
              </span>
                            <span className="text-xs text-gray-400">
                Mis à jour: {site.updatedAt ? new Date(site.updatedAt).toLocaleDateString() : 'N/A'}
              </span>
                        </div>
                    </div>
                </div>
            </td>

            {/* Colonne Statut */}
            <td className="py-4 px-6">
                <div className={`inline-flex items-center px-3 py-1.5 rounded-full border ${status.borderColor} ${status.bgColor}`}>
                    <StatusIcon className={`h-4 w-4 mr-2 ${status.color}`} />
                    <span className={`text-sm font-medium ${status.color}`}>
            {status.label}
          </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    {status.description}
                </p>
            </td>

            {/* Colonne En ligne */}
            <td className="py-4 px-6">
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">
              {uptimePercentage.toFixed(1)}%
            </span>
                        <span className="text-xs text-gray-500">
              30 jours
            </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full ${
                                uptimePercentage >= 99 ? 'bg-green-500' :
                                    uptimePercentage >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(uptimePercentage, 100)}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>0%</span>
                        <span>100%</span>
                    </div>
                </div>
            </td>

            {/* Colonne Visites aujourd'hui */}
            <td className="py-4 px-6">
                <div className="space-y-1">
                    <div className="flex items-center">
                        <BarChart3 className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-lg font-semibold text-gray-900">
              {formatNumber(site.todayVisites)}
            </span>
                    </div>
                    <div className="text-xs">
                        {site.todayVisitesChange >= 0 ? (
                            <span className="text-green-600 flex items-center">
                <span className="font-medium">+{site.todayVisitesChange || '0'}%</span>
                <span className="ml-1 text-gray-500">vs hier</span>
              </span>
                        ) : (
                            <span className="text-red-600 flex items-center">
                <span className="font-medium">{site.todayVisitesChange || '0'}%</span>
                <span className="ml-1 text-gray-500">vs hier</span>
              </span>
                        )}
                    </div>
                </div>
            </td>

            {/* Colonne Total visites */}
            <td className="py-4 px-6">
                <div className="space-y-1">
          <span className="text-lg font-semibold text-gray-900">
            {formatNumber(site.totalVisites)}
          </span>
                    <div className="text-xs text-gray-500">
                        Depuis le {site.createdAt ? new Date(site.createdAt).toLocaleDateString() : 'N/A'}
                    </div>
                </div>
            </td>

            {/* Colonne Actions */}
            <td className="py-4 px-6">
                <div className="relative">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => window.open(site.url, '_blank')}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Visiter le site"
                        >
                            <ExternalLink className="h-5 w-5" />
                        </button>

                        <button
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Voir les statistiques"
                        >
                            <BarChart3 className="h-5 w-5" />
                        </button>

                        <button
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Paramètres"
                        >
                            <Settings className="h-5 w-5" />
                        </button>

                        <button
                            onClick={() => setShowActions(!showActions)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative"
                            title="Plus d'options"
                        >
                            <MoreVertical className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Menu déroulant des actions */}
                    {showActions && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowActions(false)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                                <div className="py-1">
                                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                        Actualiser les données
                                    </button>
                                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                        Voir les logs
                                    </button>
                                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                        Tester la connexion
                                    </button>
                                    <div className="border-t border-gray-200 my-1"></div>
                                    <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                                        Désactiver la surveillance
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

// Note: Vous devez importer Globe depuis lucide-react
